"use client";

import { migrationCreate } from "../services/migration/migrationCreate";
import { CreateMigrateParams } from "../services/migration/types";
import { useState } from "react";
import { isYamlFile, isZipFile, yamlToDataUri, zipToDataUri } from "../utils/format-utils";

import type { MigrationFormData, MigrationResponse } from "../types/migrations";
import { extractApiErrorMessage } from "../utils/error-handling";
import { handleUnauthorized } from "../services/handleErrorServices";

interface UseMigrationProcessProps {
  accountId: string;
}

interface UseMigrationProcessResult {
  isLoading: boolean;
  activeStep: number;
  stepperState: "default" | "loading" | "success";
  migrationResult: MigrationResponse | null;
  toastMessage: string;
  showToast: boolean;
  setShowToast: (value: boolean) => void;
  startMigration: (formData: MigrationFormData, file: File, logout?: () => void) => Promise<boolean>;
  setActiveStep: (step: number) => void;
  setStepperState: (state: "default" | "loading" | "success") => void;
  setMigrationResult: any;
}

/**
 * Custom hook for handling the migration process
 */
export function useMigrationProcess({ accountId }: UseMigrationProcessProps): UseMigrationProcessResult {
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [stepperState, setStepperState] = useState<"default" | "loading" | "success">("default");
  const [migrationResult, setMigrationResult] = useState<MigrationResponse | null>(null);
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  // setIsLoading(true);
  const startMigration = async (formData: MigrationFormData, file: File, logout?: () => void): Promise<boolean> => {
    if (!accountId) {
      setToastMessage("Account ID not found. Please log in again.");
      setShowToast(true);
      return false;
    }

    // setActiveStep(1); // Go to processing step
    setStepperState("loading");

    try {
      // setIsLoading(true);
      let dataUri: string;

      // Detect file type and convert to data URI
      if (isYamlFile(file)) {
        const fileContent = await file.text();
        dataUri = yamlToDataUri(fileContent);
      } else if (isZipFile(file)) {
        dataUri = await zipToDataUri(file);
      } else {
        // If not YAML or ZIP, show error message
        setIsLoading(false);
        setStepperState("default");
        setToastMessage("Unsupported file format. Please upload a YAML or ZIP file.");
        setShowToast(true);
        setActiveStep(0);
        return false;
      }
      // Prepare parameters according to API structure
      const params: CreateMigrateParams = {
        content: dataUri,
        migrationName: formData.projectName,
        originSpecType: formData.sourceSystem,
        destinationSpecType: formData.destinationSystem,
        prompt: "Convert this YAML file to Apigee xml format.",
        uploadToSource: true,
        sourceFileName: file.name,
      };

      // Use accountId from hook
      const headers: any = {
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-AI-PROVIDER": formData.llmModel,
        "X-AI-MODEL": formData.versionModel,
        "X-AI-REGION": formData.region,
        "X-AI-TEMPERATURE": formData.temperature,
        "X-RESPONSE-FORMAT": "zip",
      };
      setStepperState("loading");
      const response = await migrationCreate(params, headers);
      if (response.success) {
        setIsLoading(false);
        setMigrationResult(response);
        setStepperState("success");
        setActiveStep(1); // Go to results step
        return true;
      } else {
        handleUnauthorized(response, logout);
        setToastMessage(response.error || "An error occurred during migration");
        setShowToast(true);
        setActiveStep(0);
        setStepperState("default");
        return false;
      }
    } catch (error) {
      let errorMessage = extractApiErrorMessage(error);

      if (error instanceof TypeError && error.message.includes("network")) {
        errorMessage = "Network error. Please check your internet connection.";
      }

      setIsLoading(false);
      setStepperState("default");
      setToastMessage(errorMessage);
      setShowToast(true);
      setActiveStep(0);
      return false;
    }
  };

  return {
    isLoading,
    activeStep,
    stepperState,
    migrationResult,
    toastMessage,
    showToast,
    setShowToast,
    startMigration,
    setActiveStep,
    setStepperState,
    setMigrationResult,
  };
}
